import * as React from 'react';
import {
  MdAdd,
  MdSearch,
  MdClose,
  MdCheckCircle,
  MdVisibility,
  MdEdit,
  MdPauseCircle,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { getEligibilitylist, refreshEligibilityBatches } from '../../Services/Claim360Service';
import ExportListButton from '../../Components/Claim360/ExportListButton';

// status is the mental health coverage (or the batch verdict); state is the batch progress before results arrive.
const getStatusLabel = (row) => {
  const status = row.status
    || (row.payerRejected ? 'Payer Rejected' : null)
    || row.state
    || 'Pending';

  return String(status)
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b[a-z]/g, (character) => character.toUpperCase());
};

// Same cadence as the reports page: fast while Stedi is still working on a batch, slow otherwise.
const ACTIVE_POLL_INTERVAL_MS = 15000;
const IDLE_POLL_INTERVAL_MS = 60000;

// A batch check Stedi may still finish; mirrors the backend refresh (unfinished and under 25 hours old).
const BATCH_DONE_STATES = ['COMPLETED', 'COMPLETED_WITH_ERRORS', 'VALIDATION_FAILED'];
const BATCH_REFRESH_WINDOW_MS = 25 * 60 * 60 * 1000;
const isPendingBatch = (row) => row.source === 'BATCH'
  && !BATCH_DONE_STATES.includes(row.state)
  && Date.now() - new Date(row.createdOn).getTime() < BATCH_REFRESH_WINDOW_MS;

export default function EligibilityVerificationList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewEligibility = permissionHas(userPermissionCodes, PermissionCodes.ELIGIBILITY_VERIFICATION_VIEW, decodedToken);
  const canCreateEligibility = permissionHas(userPermissionCodes, PermissionCodes.ELIGIBILITY_VERIFICATION_CREATE, decodedToken);
  const canEditEligibility = permissionHas(userPermissionCodes, PermissionCodes.ELIGIBILITY_VERIFICATION_EDIT, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loading, setLoading] = React.useState(false);

  const handleCreateEligibility = React.useCallback(() => {
    navigate(APPRoutes.ELIGIBILITY_VERIFICATION_FORM);
  }, [navigate]);

  // silent: background poll, no loader and no error toast; otherwise the table shows its loader.
  const loadEligibilityVerifications = React.useCallback(async ({ silent = false } = {}) => {
    if (!token) {
      return;
    }

    if (!silent) setLoading(true);
    try {
      const response = await getEligibilitylist(token);
      const payload = response?.data ?? response;
      const recordCollections = [
        payload,
        payload?.eligibility_logs,
        payload?.eligibilityLogs,
        payload?.eligibility_logs?.items,
        payload?.items,
        payload?.records,
        payload?.logs,
        payload?.results,
        payload?.result,
        payload?.data,
        payload?.data?.eligibility_logs,
        payload?.data?.eligibilityLogs,
        payload?.data?.logs,
        payload?.data?.items,
        payload?.data?.records,
      ];
      const eligibilityRecords = recordCollections.find(Array.isArray);

      if (!Array.isArray(eligibilityRecords)) {
        throw new Error('The eligibility API response did not contain a records array. Check the response in the browser Network tab.');
      }

      const mappedRecords = eligibilityRecords.map((record) => ({
        ...record,
        status_name: getStatusLabel(record),
        subscriber_name: record.patientName || '-',
        organization_name: record.organizationName || '-',
        member_id: record.memberId || '-',
        payer_id: record.payerId || '-',
        payer_name: record.payerName || record.payerId || '-',
        created_by_name: record.createdByName || '-',
        created_on: record.createdOn ?? null,
      }));

      setRows(mappedRecords);
      setStatusSummary({
        total: mappedRecords.length,
        active: mappedRecords.filter((record) => /^active/i.test(record.status_name)).length,
        inactive: mappedRecords.filter((record) => /failed|inactive|rejected/i.test(record.status_name)).length,
      });
    } catch (error) {
      console.error('Failed to load eligibility verifications:', error);
      if (silent) return;
      toast.error(error.message || 'Unable to load eligibility verifications.');
      setRows([]);
      setStatusSummary({ total: 0, active: 0, inactive: 0 });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadEligibilityVerifications();
    }
  }, [token, loadEligibilityVerifications]);

  const hasPendingBatch = React.useMemo(() => rows.some(isPendingBatch), [rows]);
  const pollInFlight = React.useRef(false);

  // While a batch is unfinished, pull its Stedi results before each reload; skip a tick if the last one is still running.
  React.useEffect(() => {
    if (!token) return undefined;
    const timer = setInterval(async () => {
      if (pollInFlight.current) return;
      pollInFlight.current = true;
      try {
        if (hasPendingBatch) await refreshEligibilityBatches(token);
      } catch (error) {
        console.error('Failed to refresh batch eligibility results:', error);
      } finally {
        await loadEligibilityVerifications({ silent: true });
        pollInFlight.current = false;
      }
    }, hasPendingBatch ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [token, hasPendingBatch, loadEligibilityVerifications]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus =
        status === 'All' || row.status_name === status;
      const matchesSearch =
        !term ||
        [row.subscriber_name, row.member_id, row.payer_id, row.payer_name, row.organization_name, row.created_by_name]
          .some((value) => String(value || '').toLowerCase().includes(term));

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const statusDropdownOptions = React.useMemo(() => [
    { value: 'All', label: 'All' },
    ...Array.from(new Set(rows.map((row) => row.status_name).filter(Boolean)))
      .map((option) => ({ value: option, label: option })),
  ], [rows]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Total Eligibility Checks',
        value: statusSummary.total,
        icon: <MdAdd size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active MH Coverage',
        value: statusSummary.active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive / Rejected',
        value: statusSummary.inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ],
    [statusSummary],
  );

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'subscriber_name',
        label: 'Subscriber',
        minWidth: 220,
        format: (value, row) => canViewEligibility ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() => navigate(APPRoutes.ELIGIBILITY_VERIFICATION_VIEW.replace(':id', row.id), { state: { eligibility: row } })}
          >
            <div className="fw-semibold">{value || '-'}</div>
          </button>
        ) : (
            <div className="fw-semibold text-dark">{value || '-'}</div>
        ),
      },
      {
        id: 'organization_name',
        label: 'Organization',
        minWidth: 180,
      },
      {
        id: 'payer_name',
        label: 'Payer',
        minWidth: 200,
      },
      {
        id: 'status_name',
        label: 'MH Coverage',
        minWidth: 160,
      },
    ];

    if (canViewEligibility || canEditEligibility) {
      cols.push({
        id: 'actions',
        label: 'Action',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewEligibility ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => navigate(APPRoutes.ELIGIBILITY_VERIFICATION_VIEW.replace(':id', row.id), { state: { eligibility: row } })}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditEligibility ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(
                  APPRoutes.ELIGIBILITY_VERIFICATION_EDIT.replace(':id', row.id),
                  { state: { eligibility: row } },
                )}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    canViewEligibility,
    canEditEligibility,
  ]);

  return (
    <>
      <PageTitle title="Eligibility Verification - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Check Eligibility Verification</h4>
          <p className="text-secondary mb-0 small">
            Manage subscriber eligibility verification records.
          </p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 mt-md-0 mt-2">
          <ExportListButton kind="eligibility" rows={filteredRows} disabled={loading} />
          {canCreateEligibility ? (
            <button
              onClick={handleCreateEligibility}
              type="button"
              className="btn hc-bg-primary w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
            >
              <MdAdd size={20} />
              <span>Check Eligibility</span>
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 44, height: 44, borderRadius: 12 }} />
                <div className="w-100">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 24, width: '45%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search subscriber, payer, member or organization"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">

               <SearchableDropdown
                  placeholder="Select status"
                  options={statusDropdownOptions}
                  value={status}
                  onChange={setStatus}
                  showSearch={true}
                />

          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>
    </>
  );
}
